import React from 'react';

export type AmbientSubjectId = 'matematica' | 'lenguaje' | 'musica' | 'ciencias' | 'historia';

interface AmbientSubjectBackgroundProps {
  /** Id de asignatura en la ruta (`/studio/asignatura/:subjectId`) */
  subjectId: string;
  /** Tinte de marca desde SUBJECT_CATALOG — alinea cromáticamente con las tarjetas */
  subjectHue?: string;
  subjectTint?: string;
}

const AMBIENT_CONFIG: Record<
  AmbientSubjectId,
  {
    tailwindColor: string;
    glyphs: Array<{ symbol: string; className: string; animation: string }>;
  }
> = {
  matematica: {
    tailwindColor: 'text-emerald-500',
    glyphs: [
      { symbol: 'π', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--hero -right-10 top-20 font-serif', animation: 'animate-float-slow' },
      { symbol: '√x', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--mono -left-12 bottom-10 font-mono', animation: 'animate-spin-slow' },
      { symbol: '42', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--mid left-[58%] top-[38%] font-mono', animation: 'animate-float-delayed' },
      { symbol: '÷', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--small right-[18%] top-[62%] font-serif', animation: 'animate-float-slow' },
    ],
  },
  lenguaje: {
    tailwindColor: 'text-violet-500',
    glyphs: [
      { symbol: '“', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--hero -right-6 top-16 font-serif', animation: 'animate-float-slow' },
      { symbol: 'A', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--mono -left-10 bottom-12 font-serif', animation: 'animate-spin-slow' },
      { symbol: '¶', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--mid left-[62%] top-[42%] font-serif', animation: 'animate-float-delayed' },
      { symbol: '”', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--small right-[22%] top-[68%] font-serif', animation: 'animate-float-slow' },
    ],
  },
  musica: {
    tailwindColor: 'text-sky-500',
    glyphs: [
      { symbol: '𝄞', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--hero -right-8 top-20', animation: 'animate-float-slow' },
      { symbol: '♫', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--mono -left-12 bottom-8', animation: 'animate-spin-slow' },
      { symbol: '♩', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--mid left-[60%] top-[40%]', animation: 'animate-float-delayed' },
      { symbol: '♪', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--small right-[20%] top-[64%]', animation: 'animate-float-slow' },
    ],
  },
  ciencias: {
    tailwindColor: 'text-teal-500',
    glyphs: [
      { symbol: 'H₂O', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--hero -right-10 top-20 font-mono', animation: 'animate-float-slow' },
      { symbol: '⚛', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--mono -left-12 bottom-10', animation: 'animate-spin-slow' },
      { symbol: 'O₂', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--mid left-[58%] top-[38%] font-mono', animation: 'animate-float-delayed' },
      { symbol: '🧪', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--small right-[18%] top-[62%]', animation: 'animate-float-slow' },
    ],
  },
  historia: {
    tailwindColor: 'text-amber-500',
    glyphs: [
      { symbol: 'XXVI', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--hero -right-10 top-20 font-serif', animation: 'animate-float-slow' },
      { symbol: 'V', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--mono -left-10 bottom-12 font-serif', animation: 'animate-spin-slow' },
      { symbol: '🧭', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--mid left-[60%] top-[40%]', animation: 'animate-float-delayed' },
      { symbol: 'I', className: 'ambient-subject-bg__glyph ambient-subject-bg__glyph--small right-[22%] top-[66%] font-serif', animation: 'animate-float-slow' },
    ],
  },
};

function resolveAmbientSubject(subjectId: string): AmbientSubjectId | null {
  if (subjectId in AMBIENT_CONFIG) return subjectId as AmbientSubjectId;
  return null;
}

export function AmbientSubjectBackground({
  subjectId,
  subjectHue,
  subjectTint,
}: AmbientSubjectBackgroundProps) {
  const ambientId = resolveAmbientSubject(subjectId);
  if (!ambientId) return null;

  const current = AMBIENT_CONFIG[ambientId];

  return (
    <div
      className="ambient-subject-bg pointer-events-none fixed inset-0 z-0 overflow-hidden select-none"
      aria-hidden
      style={
        {
          '--ambient-hue': subjectHue ?? 'var(--sala-accent-neon)',
          '--ambient-tint': subjectTint ?? 'transparent',
        } as React.CSSProperties
      }
    >
      <div className="ambient-subject-bg__wash" />

      {current.glyphs.map((glyph, index) => (
        <div
          key={`${ambientId}-${index}`}
          className={`absolute font-bold leading-none transition-colors duration-500 ${glyph.className} ${glyph.animation} ${current.tailwindColor}`}
        >
          {glyph.symbol}
        </div>
      ))}
    </div>
  );
}
